import { createContext, useContext, useRef } from "react";

import { useStore } from "zustand";

import {
  type WellDetailsState,
  type WellDetailsStore,
  type WellDetailsStoreApi,
  createWellDetailsStore
} from "./wellDetailsStore";

export const WellDetailsStoreContext = createContext<Maybe<WellDetailsStoreApi>>(null);

interface WellDetailsStoreProviderProps {
  readonly children: React.ReactNode;
  readonly overrides?: Partial<WellDetailsState>;
}

function WellDetailsStoreProvider({
  children,
  overrides
}: WellDetailsStoreProviderProps) {
  const storeRef = useRef<Maybe<WellDetailsStoreApi>>(null);
  if (storeRef.current === null) {
    storeRef.current = createWellDetailsStore(overrides);
  }
  return (
    <WellDetailsStoreContext.Provider value={storeRef.current}>
      {children}
    </WellDetailsStoreContext.Provider>
  );
}

/**
 * Hook to select state from the nearest WellDetailsStore.
 *
 * Usage (in a Container or Logic Hook — never in a Presenter):
 *   const currentGroup = useWellDetailsStore(selectCurrentGroup);
 *
 * For compound selectors that return new object literals, wrap the selector
 * with `useShallow` at the call site to avoid re-renders on every store update:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const state = useWellDetailsStore(useShallow(selectWellDetailsState));
 */
function useWellDetailsStore<T>(selector: (state: WellDetailsStore) => T): T {
  const store = useContext(WellDetailsStoreContext);
  if (!store) {
    throw new Error(
      "useWellDetailsStore must be used within a <WellDetailsStoreProvider>"
    );
  }
  return useStore(store, selector);
}

export { WellDetailsStoreProvider, useWellDetailsStore };
